import React, { memo, useMemo, useRef, useState } from "react";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { Tabs } from "antd";
import { EmptyIcon } from "@/icons";
import "./index.less";

const noop = () => {};

const GpsIcon = ({ size = 16, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    {...props}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M7.99935 5.33268C6.52659 5.33268 5.33268 6.52659 5.33268 7.99935C5.33268 9.47211 6.52659 10.666 7.99935 10.666C9.47211 10.666 10.666 9.47211 10.666 7.99935C10.666 6.52659 9.47211 5.33268 7.99935 5.33268ZM6.66602 7.99935C6.66602 7.26297 7.26297 6.66602 7.99935 6.66602C8.73573 6.66602 9.33268 7.26297 9.33268 7.99935C9.33268 8.73573 8.73573 9.33268 7.99935 9.33268C7.26297 9.33268 6.66602 8.73573 6.66602 7.99935Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8.66602 0.666016V2.03597C11.4471 2.3434 13.6553 4.5516 13.9627 7.33268H15.3327V8.66602H13.9627C13.6553 11.4471 11.4471 13.6553 8.66602 13.9627V15.3327H7.33268V13.9627C4.5516 13.6553 2.3434 11.4471 2.03597 8.66602H0.666016V7.33268H2.03597C2.3434 4.5516 4.5516 2.3434 7.33268 2.03597V0.666016H8.66602ZM7.99935 3.33268C5.42202 3.33268 3.33268 5.42202 3.33268 7.99935C3.33268 10.5767 5.42202 12.666 7.99935 12.666C10.5767 12.666 12.666 10.5767 12.666 7.99935C12.666 5.42202 10.5767 3.33268 7.99935 3.33268Z"
      fill="currentColor"
    />
  </svg>
);

const CleanIcon = ({ size = 24, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    {...props}
  >
    <path
      d="M14.1215 15.5354L12 13.4138L9.8783 15.5355L8.46409 14.1213L10.5858 11.9996L8.46469 9.87857L9.87891 8.46436L12 10.5854L14.1209 8.46445L15.5352 9.87866L13.4142 11.9996L15.5358 14.1212L14.1215 15.5354Z"
      fill="#252525"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12 1C5.92487 1 1 5.92487 1 12C1 18.0751 5.92487 23 12 23C18.0751 23 23 18.0751 23 12C23 5.92487 18.0751 1 12 1ZM3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12Z"
      fill="#252525"
    />
  </svg>
);

/**
 * RegionPicker (Explore redesign)
 *
 * Pure layout component matching the Explore Figma "Regional Settings" panel.
 */
const RegionPicker = ({
  currentCity, // 当前城市
  searchValue, // 受控搜索值
  onSearchValueChange, // 搜索值变更回调
  searchRegion = noop,
  searchResults = [],
  regions = [],
  getPosition = noop,
  onCityClick = noop,
}) => {
  const { t } = useTranslation();
  const pickerRef = useRef(null);
  const detectedInputRef = useRef(null);
  const [internalSearch, setInternalSearch] = useState("");

  const query = searchValue !== undefined ? searchValue : internalSearch;
  const hasQuery = Boolean((query || "").trim());

  const setQuery = (nextValue) => {
    if (onSearchValueChange) onSearchValueChange(nextValue);
    if (searchValue === undefined) setInternalSearch(nextValue);
    searchRegion(nextValue);
  };

  const DetectedCity = useMemo(() => {
    return currentCity
      ? `${t("Header.Detected")}:${[
          currentCity.name,
          currentCity.country_title,
        ].join(", ")}`
      : `${t("Header.Detected")}:`;
  }, [currentCity, t]);

  const [currentTab, setCurrentTab] = useState("0");

  const sortedRegionGroups = useMemo(() => {
    return (regions || []).map((region) => {
      const entries = Object.entries(region?.regions || {}).sort(([a], [b]) =>
        String(a).toUpperCase().localeCompare(String(b).toUpperCase())
      );
      return { region, entries };
    });
  }, [regions]);

  return (
    <section ref={pickerRef} className="ui-region-picker" aria-label={t("Header.SelectYourRegion")}>
      <div className="ui-region-picker__title">{t("Header.SelectYourRegion")}</div>

      <div
        className={cn("ui-region-picker__detected", { "is-query": hasQuery })}
        role="search"
      >
        <button
          type="button"
          className={cn("ui-region-picker__detectedIcon", {
            "is-query": hasQuery,
          })}
          aria-label="Detect location"
          onClick={getPosition}
        >
          <GpsIcon />
        </button>
        <input
          ref={detectedInputRef}
          className="ui-region-picker__detectedInput"
          type="search"
          placeholder={DetectedCity}
          aria-label={DetectedCity}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {hasQuery && (
          <button
            type="button"
            className="ui-region-picker__detectedClear"
            aria-label="Clear"
            onClick={() => {
              setQuery("");
              if (detectedInputRef.current) detectedInputRef.current.focus();
            }}
          >
            <CleanIcon />
          </button>
        )}
      </div>

      <div className="ui-region-picker__sectionTitle">
        {hasQuery ? t("Header.SearchResults") : t("Header.Popular")}
      </div>

      {hasQuery ? (
        <div
          className="ui-region-picker__list ui-region-picker__searchList"
          role="list"
        >
          {searchResults.map((item) => {
            const id = typeof item === "string" ? item : item.id;
            return (
              <button
                key={id}
                type="button"
                className="ui-region-picker__searchItem"
                onClick={() => onCityClick(item)}
              >
                {item.name},{item.country_title}
              </button>
            );
          })}

          {searchResults.length === 0 && (
            <div className="ui-head-bar__keywordEmpty" role="status">
              <EmptyIcon />
              <p> {t("Header.No results found")} </p>
            </div>
          )}
        </div>
      ) : (
        <Tabs
          activeKey={currentTab}
          onTabClick={(key) => {
            setCurrentTab(key);
          }}
          popupClassName="ui-region-picker__tabsPopup"
          tabBarExtraContent={{
            left:
              Number(currentTab) === 0 ? null : (
                <div
                  className="arrowBtn"
                  onClick={() => {
                    const key = Number(currentTab) - 1;
                    setCurrentTab(String(key));
                  }}
                >
                  <i className="icon icon-arrow-left"></i>
                </div>
              ),
            right:
              Number(currentTab) === regions?.length - 1 ? null : (
                <div
                  className="arrowBtn"
                  onClick={() => {
                    const key = Number(currentTab) + 1;
                    setCurrentTab(String(key));
                  }}
                >
                  <i className="icon icon-arrow-right"></i>
                </div>
              ),
          }}
        >
          {sortedRegionGroups.map(({ region, entries }, index) => (
            <Tabs.TabPane tab={region.title} key={index}>
              <div className="ui-region-picker__list" role="list">
                {entries.map(([letter, cities]) => (
                  <div
                    key={letter}
                    className="ui-region-picker__group"
                    role="listitem"
                  >
                    <div className="ui-region-picker__groupLetter">
                      {letter}
                    </div>
                    <div className="ui-region-picker__cities">
                      {(cities || []).map((city) => (
                        <button
                          key={city.id}
                          type="button"
                          className="ui-region-picker__city"
                          onClick={() => onCityClick(city)}
                        >
                          {city.name}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Tabs.TabPane>
          ))}
        </Tabs>
      )}
    </section>
  );
};

export default memo(RegionPicker);
